import { SettingType, UI_UNSET_VALUE } from '../../types/settings'
import { ISettingsAdapterArgs } from './settingsAdapter'
import { schemas } from './schemas'

export type SettingAdapterConfig = Omit<ISettingsAdapterArgs<any, any, any>, 'toRawValue' | 'toUiValue'>

const baseAdapterConfig: Pick<
  SettingAdapterConfig,
  'skipFormPopulateOnLoad' | 'shouldUseRawValue' | 'initialUiValue' | 'shouldTreatAsUnset'
> = {
  initialUiValue: UI_UNSET_VALUE,
  skipFormPopulateOnLoad: false,
  shouldUseRawValue: rawValue => typeof rawValue !== 'undefined' && rawValue !== null,
  shouldTreatAsUnset: rawValue => rawValue === null
}

const baseDBAdapterConfig: Pick<
  SettingAdapterConfig,
  'skipFormPopulateOnLoad' | 'shouldUseRawValue' | 'initialUiValue' | 'type' | 'shouldTreatAsUnset'
> = {
  ...baseAdapterConfig,
  type: SettingType.DB
}

const baseQNSAdapterConfig: Pick<
  SettingAdapterConfig,
  'skipFormPopulateOnLoad' | 'shouldUseRawValue' | 'initialUiValue' | 'type' | 'defaultRawValue' | 'shouldTreatAsUnset'
> = {
  ...baseAdapterConfig,
  defaultRawValue: null,
  type: SettingType.QNS,
  shouldUseRawValue: rawValue => typeof rawValue !== 'undefined' && rawValue !== null && rawValue !== ''
}

const adapterConfigs = {
  enable_quarantine: {
    ...baseDBAdapterConfig,
    defaultRawValue: '0',
    inputName: 'isEnabledMessagesForIntentAnalysis',
    variable: 'enable_quarantine',
    schema: schemas.radio_on_off
  },
  allow_temporary_passcode: {
    ...baseDBAdapterConfig,
    defaultRawValue: '0',
    inputName: 'isTempPasscodeEnabled',
    variable: 'allow_temporary_passcode',
    schema: schemas.radio_on_off
  },
  custom_inbound_schedule: {
    ...baseQNSAdapterConfig,
    initialUiValue: { mon: [], tue: [], wed: [], thu: [], fri: [], sat: [], sun: [] },
    inputName: 'customInboundSchedule',
    variable: 'custom_inbound_schedule',
    schema: schemas.custom_schedule
  },
  inbound_schedule: {
    ...baseQNSAdapterConfig,
    defaultRawValue: 'never',
    inputName: 'defaultInterval',
    variable: 'inbound_schedule',
    schema: schemas.inbound_schedule
  },
  allow_override: {
    ...baseQNSAdapterConfig,
    defaultRawValue: '0',
    inputName: 'isAllowSpecifyInterval',
    variable: 'allow_override',
    schema: schemas.radio_on_off
  },
  allow_whitelist: {
    ...baseQNSAdapterConfig,
    inputName: 'allowWhitelist',
    variable: 'allow_whitelist',
    schema: schemas.radio_on_off
  },
  custom_outbound_schedule: {
    ...baseQNSAdapterConfig,
    initialUiValue: { mon: [], tue: [], wed: [], thu: [], fri: [], sat: [], sun: [] },
    inputName: 'customOutboundSchedule',
    variable: 'custom_outbound_schedule',
    schema: schemas.custom_schedule
  },
  outbound_schedule: {
    ...baseQNSAdapterConfig,
    inputName: 'outboundSchedule',
    variable: 'outbound_schedule',
    schema: schemas.outbound_schedule
  },
  qns_timezone: {
    ...baseQNSAdapterConfig,
    inputName: 'qnsTimezone',
    variable: 'timezone',
    schema: schemas.timezone
  },
  default_managed_scan_policy: {
    ...baseDBAdapterConfig,
    defaultRawValue: 'scan',
    inputName: 'defaultManagedScanPolicy',
    variable: 'default_managed_scan_policy',
    schema: schemas.scan_policy
  },
  default_unmanaged_scan_policy: {
    ...baseDBAdapterConfig,
    defaultRawValue: 'scan',
    inputName: 'defaultUnmanagedScanPolicy',
    variable: 'default_unmanaged_scan_policy',
    schema: schemas.scan_policy
  },
  default_user_exempt_enabled: {
    ...baseDBAdapterConfig,
    defaultRawValue: '1',
    inputName: 'defaultUserExemptEnabled',
    variable: 'default_user_exempt_enabled',
    schema: schemas.setting_0_1_2
  },
  default_user_block_enabled: {
    ...baseDBAdapterConfig,
    defaultRawValue: '1',
    inputName: 'defaultUserBlockEnabled',
    variable: 'default_user_block_enabled',
    schema: schemas.radio_on_off
  },
  default_user_deliver_enabled: {
    ...baseDBAdapterConfig,
    defaultRawValue: '0',
    inputName: 'defaultUserDeliverEnabled',
    variable: 'default_user_deliver_enabled',
    schema: schemas.radio_on_off
  },
  default_user_quar_deliver_enabled: {
    ...baseDBAdapterConfig,
    defaultRawValue: '1',
    inputName: 'defaultUserQuarDeliverEnabled',
    variable: 'default_user_quar_deliver_enabled',
    schema: schemas.radio_on_off
  },
  default_timezone: {
    ...baseDBAdapterConfig,
    defaultRawValue: 'America/Los_Angeles',
    inputName: 'defaultTimezone',
    variable: 'default_timezone',
    schema: schemas.timezone
  },
  // TODO: remove duplicate
  outbound_quarantine_interval: {
    ...baseQNSAdapterConfig,
    defaultRawValue: 'never',
    inputName: 'outboundQuarantineInterval',
    variable: 'outbound_schedule',
    schema: schemas.outbound_schedule
  },
  outbound_email_ids: {
    ...baseQNSAdapterConfig,
    defaultRawValue: [''],
    inputName: 'outboundEmailIds',
    variable: 'outbound_email_ids',
    schema: schemas.required_csv_email_list
  },
  sender_notification_enable: {
    ...baseDBAdapterConfig,
    defaultRawValue: '0',
    inputName: 'senderNotificationEnable',
    variable: 'sender_notification_enable',
    schema: schemas.radio_on_off
  },
  sender_notification_address: {
    ...baseDBAdapterConfig,
    defaultRawValue: '',
    inputName: 'senderNotificationAddress',
    variable: 'sender_notification_address',
    schema: schemas.optional_email
  },
  sender_notification_subject: {
    ...baseDBAdapterConfig,
    defaultRawValue: '',
    inputName: 'senderNotificationSubject',
    variable: 'sender_notification_subject',
    schema: schemas.optional_string
  },
  sender_notification_template: {
    ...baseDBAdapterConfig,
    defaultRawValue: '',
    inputName: 'senderNotificationTemplate',
    variable: 'sender_notification_template',
    schema: schemas.optional_string
  },
  reject_notification_address: {
    ...baseDBAdapterConfig,
    defaultRawValue: '',
    inputName: 'rejectNotificationAddress',
    variable: 'reject_notification_address',
    schema: schemas.optional_email
  },
  reject_notification_subject: {
    ...baseDBAdapterConfig,
    defaultRawValue: '',
    inputName: 'rejectNotificationSubject',
    variable: 'reject_notification_subject',
    schema: schemas.optional_string
  },
  reject_notification_template: {
    ...baseDBAdapterConfig,
    defaultRawValue: '',
    inputName: 'rejectNotificationTemplate',
    variable: 'reject_notification_template',
    schema: schemas.optional_string
  },
  outbound_spam_notification_address: {
    ...baseDBAdapterConfig,
    defaultRawValue: '',
    inputName: 'outboundSpamNotificationAddress',
    variable: 'outbound_spam_notification_address',
    schema: schemas.required_csv_email_list
  }
}

export const settings: Record<keyof typeof adapterConfigs, SettingAdapterConfig> = adapterConfigs
